/**
 * ProConItem — single pro/con row inside a site column panel. Displays
 * the category badge + impact-level pill, the description, and an
 * action cluster (upvote, edit, delete).
 *
 * Read-only when no handlers are given (MYD-502): a presentation slide shows
 * the same item without the actions, and `size="slide"` scales the type for
 * the 1280x720 stage. The Pros/Cons tab passes all three and is unchanged.
 *
 * Linear: MYD-223 (H1).
 */

import { Pencil, Trash2, ThumbsUp } from 'lucide-react'
import { Badge } from 'components/ui/badge'
import { cn } from 'lib/utils'

export const IMPACT_COLORS: Record<string, string> = {
	HIGH: 'bg-red-50 text-red-700 border-red-200',
	MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
	LOW: 'bg-gray-100 text-gray-600 border-gray-200',
}

interface ProConItemProps {
	item: any
	onVote?: (itemId: string) => void
	onEdit?: (item: any) => void
	onDelete?: (itemId: string) => void
	size?: 'card' | 'slide'
}

const ProConItem = ({ item, onVote, onEdit, onDelete, size = 'card' }: ProConItemProps) => {
	const slide = size === 'slide'
	const actions = Boolean(onVote || onEdit || onDelete)
	return (
		<div className={cn('rounded-md border border-gray-100 hover:border-gray-200 bg-white', slide ? 'px-4 py-3' : 'px-3 py-2.5')}>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<div className={cn('flex items-center gap-2 flex-wrap', slide ? 'mb-1.5' : 'mb-1')}>
						<Badge className={cn('bg-gray-100 text-gray-600', slide ? 'text-[14px]' : 'text-[10px]')}>{item.category}</Badge>
						{item.impactLevel && (
							<Badge
								className={cn(
									'border',
									slide ? 'text-[14px]' : 'text-[10px]',
									IMPACT_COLORS[item.impactLevel] || IMPACT_COLORS.LOW,
								)}
							>
								{item.impactLevel}
							</Badge>
						)}
					</div>
					<p className={cn('text-gray-800 break-words', slide ? 'text-[20px] leading-snug' : 'text-sm')}>{item.description}</p>
				</div>
				{actions && (
					<div className="flex items-center gap-1 shrink-0">
						<button
							type="button"
							onClick={() => onVote?.(item._id)}
							title="Upvote"
							aria-label="Upvote"
							className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-gray-500 hover:text-[#0080fe] hover:bg-gray-50"
						>
							<ThumbsUp className="w-3 h-3" />
							{item.votes ?? 0}
						</button>
						<button
							type="button"
							onClick={() => onEdit?.(item)}
							title="Edit"
							aria-label="Edit"
							className="p-1 rounded text-gray-400 hover:text-[#0080fe] hover:bg-gray-50"
						>
							<Pencil className="w-3.5 h-3.5" />
						</button>
						<button
							type="button"
							onClick={() => onDelete?.(item._id)}
							title="Delete"
							aria-label="Delete"
							className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-gray-50"
						>
							<Trash2 className="w-3.5 h-3.5" />
						</button>
					</div>
				)}
			</div>
		</div>
	)
}

export default ProConItem
